import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";

import { useBluelagoon } from "@bluelagoon";
import { GeneratedComponentRenderer } from "@chat";
import { useAuth } from "../context/AuthContext";
// import DocumentEditor from "./components/DocumentEditor/index";
import ExcelReader from "./components/ExcelReader";
import PublicFileUpload from "./components/PublicFileUpload";
import Logo from "@ui/Logo";
import PDFDocumentTable from "../components/PDFDocumentTable";

function MainComponent({
    onSubmit,
    publicKey,
    isForPreview,
    defaultStepId,
    handleErrorLog,
    defaultExecutionId,
    testData,
    handleSetLogs,
}) {
    const { call, response, error } = useBluelagoon({
        method: "startWorkflowWithPublicKey",
    });

    const { user } = useAuth();
    const location = useLocation();

    // Prepare current user and role data
    const currentUser = React.useMemo(() => {
        if (!user) return null;
        return {
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            groups: user.groups || [],
        };
    }, [user]);

    const currentRole = React.useMemo(() => {
        if (!user?.roles || user.roles.length === 0) return null;
        // Get the first role or the primary role
        const primaryRole = user.roles[0];
        return {
            id: primaryRole || primaryRole?.role,
            name: primaryRole || primaryRole?.role,
        };
    }, [user]);

    // Function to extract search parameters from URL
    const getSearchParameters = () => {
        const searchParams = new URLSearchParams(location.search);

        if (searchParams == null || searchParams.size === 0) return null;
        const params = {};
        for (const [key, value] of searchParams.entries()) {
            params[key] = value;
        }
        return params;
    };

    const initialData = React.useMemo(
        () => testData ?? getSearchParameters(),
        [location.search, testData]
    );

    const [
        enviormentVariablesForInjection,
        setEnviormentVariablesForInjection,
    ] = useState({});
    const [componentData, setComponentData] = useState(null);
    const [config, setConfig] = useState(null);
    const [integrations, setIntegrations] = useState(null);
    const [additionalComponents, setAdditionalComponents] = useState({});
    const [stepData, setStepData] = useState(null);
    const [inputs, setInputs] = useState(null);
    const [components, setComponents] = useState([]);
    const [executionId, setExecutionId] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [stepId, setStepId] = useState(defaultStepId);
    const [isWorking, setIsWorking] = useState(false);
    const [hasValidSubscription, setHasValidSubscription] = useState(true);
    const [trialEndDate, setTrialEndDate] = useState(null);
    const refCallId = useRef(null);
    const didRun = useRef(false);

    // Use sessionStorage to track if we've already called this specific combination
    const callKey = `appbuilder-call-${publicKey}-${defaultStepId}-${defaultExecutionId}`;

    const handleOnSubmit = React.useCallback(
        async (params) => {
            setIsWorking(true);
            setCurrentStep((prev) => prev + 1);

            // Store in sessionStorage that we've submitted for this combination
            sessionStorage.setItem(callKey, "submitted");

            if (onSubmit) {
                let stepData = params.data ?? params;

                if (typeof stepData === "string") {
                    stepData = {
                        data: stepData,
                    };
                }

                onSubmit({
                    publicKey,
                    stepData,
                    executionId,
                    stepId,
                });
                setIsWorking(false);
                setCompleted(true);
                return;
            }

            const response = await call({
                publicKey,
                stepData: params.data ?? params,
                executionId,
                isForPreview,
            });
            setIsWorking(false);
        },
        [executionId, onSubmit, stepId, callKey]
    );

    const createPageUrl = React.useCallback((params) => {
        const { origin, pathname } = window.location;
        const url = new URL(pathname, origin);

        if (params) {
            for (const key of Object.keys(params)) {
                url.searchParams.set(key, params[key]);
            }
        }

        return url.toString();
    }, []);

    // useEffect(() => {
    //     // Check if we've already called or submitted for this combination
    //     const hasCalledOrSubmitted = sessionStorage.getItem(callKey);

    //     if (!hasCalledOrSubmitted) {
    //         console.log("calling", callKey);
    //         // Mark that we've called this combination
    //         sessionStorage.setItem(callKey, "called");

    //         call({
    //             publicKey,
    //             stepId: defaultStepId,
    //             executionId: defaultExecutionId,
    //         });
    //     }
    // }, [publicKey, defaultStepId, defaultExecutionId, callKey]);

    useEffect(() => {
        if (
            publicKey &&
            (!refCallId.current ||
                refCallId.current !==
                    `${publicKey}-${defaultStepId}-${defaultExecutionId}`)
        ) {
            refCallId.current = `${publicKey}-${defaultStepId}-${defaultExecutionId}`;

            const searchParams = getSearchParameters();

            call({
                publicKey,
                stepId: defaultStepId,
                executionId: defaultExecutionId,
                dontSubmit: true,
                data: searchParams,
            });
        }
    }, [publicKey, defaultStepId, defaultExecutionId]);

    useEffect(() => {
        if (response?.data?.completed && !didRun.current) {
            setCompleted(true);
        } else if (
            response?.data?.contextData?.length &&
            response.data.userPrompt
        ) {
            const data = response.data.userPrompt;
            didRun.current = true;

            setExecutionId(response.data.executionId);
            setStepId(response.data.stepId);
            setEnviormentVariablesForInjection(data.processVariables);
            setConfig({
                ...data.config,
            });

            setIntegrations(data.integrations);
            setAdditionalComponents({
                // DocumentEditor: (props) => (
                //     <DocumentEditor config={data.config} {...props} />
                // ),
                ExcelReader: (props) => (
                    <ExcelReader {...props} config={data.config} />
                ),
                FileUpload: (props) => (
                    <PublicFileUpload {...props} config={data.config} />
                ),
                PDFDocumentTable: (props) => (
                    <PDFDocumentTable {...props} config={data.config} />
                ),
            });
            setStepData(data.stepData);
            // inputs = previous step's output (second to last), not the current step
            const prevIndex = Array.isArray(data.stepData) ? data.stepData.length - 2 : -1;
            setInputs(prevIndex >= 0 ? data.stepData[prevIndex] ?? null : null);
            setComponentData({
                code: data.code,
                name: "App",

                props: { name: "string" },
            });

            // Set components from response data if available
            if (data.components) {
                setComponents(data.components);
            }
        }
    }, [response]);

    useEffect(() => {
        if (error?.status === 403 && error?.data) {
            setHasValidSubscription(error.data.hasValidSubscription);
            setTrialEndDate(error.data.trialEndDate);
        }
    }, [error]);

    if (!hasValidSubscription) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center max-w-md mx-auto p-6">
                    <div className="mb-4 flex justify-center">
                        <Logo />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Trial Period Expired
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                        {trialEndDate
                            ? `Your trial period ended on ${new Date(
                                  trialEndDate
                              ).toLocaleDateString()}`
                            : "Your trial period has ended"}
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        To continue using this application, please contact the
                        administrator to activate a subscription.
                    </p>
                    <div className="text-xs text-gray-400">
                        This application is powered by Agent UI
                    </div>
                </div>
            </div>
        );
    }

    if (completed) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="mb-4 flex justify-center">
                        <Logo />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                        Workflow completed successfully
                    </h3>
                    <p className="text-sm text-gray-500">
                        This workflow is powered by Agent UI
                    </p>
                </div>
            </div>
        );
    }

    if (isWorking) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                        Working on it...
                    </h3>
                    <p className="text-sm text-gray-500">
                        This may take a few minutes...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {componentData && config ? (
                <GeneratedComponentRenderer
                    generatedComponent={componentData}
                    responseData={{ name: "Nessim" }}
                    enviormentVariablesForInjection={
                        enviormentVariablesForInjection
                    }
                    config={config}
                    integrations={integrations}
                    additionalComponents={additionalComponents}
                    stepData={stepData}
                    inputs={inputs}
                    onSubmit={handleOnSubmit}
                    onError={handleErrorLog}
                    initialData={initialData}
                    handleSetLogs={handleSetLogs}
                    createPageUrl={createPageUrl}
                    currentUser={currentUser}
                    currentRole={currentRole}
                    components={components}
                />
            ) : (
                <div className="flex items-center justify-center min-h-screen bg-gray-50">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                            Loading your application
                        </h3>
                        <p className="text-sm text-gray-500">
                            Please wait while we fetch the data...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export const AppBuilderComponent = React.memo(MainComponent);

export default function AppBuilder() {
    const { publicKey } = useParams();

    return <AppBuilderComponent publicKey={publicKey} />;
}
